import React from "react";

import {
  DEFAULT_EGG_LIGHTS,
  DEFAULT_EGG_REDSHIFT,
  DEFAULT_EGG_WARMTH,
  DEFAULT_PALETTE,
  LANDING_PHOTO_FADE_IN_MS,
  LANDING_PHOTO_FADE_OUT_MS,
} from "../constants";

// Creative mode session state. No persistence — every entry into
// creative starts fresh, and switching marcs reseeds the per-marc
// values from the new reference.
//
// Marc swap is a cross-fade. Two marc IDs flow through:
//
//   selectedMarcId  — the user's pick. Drives the egg side (so the egg
//                     pose tween + reference fetch begin as soon as the
//                     user clicks).
//   displayedMarcId — what the easel + onion currently SHOW. Lags
//                     selectedMarcId by FADE_OUT_MS during a swap, so
//                     the marcId/transform snap on the photo + onion
//                     happens under cover of opacity 0. First pick
//                     (no prior marc) and same-marc re-pick skip the
//                     transition entirely.
//
// transitionPhase drives the opacity dampening on both surfaces:
//   'idle' — opacity follows the user (or 1 for the easel)
//   'out'  — easel photoVisible=false, onion opacity=0; CSS transition
//            fades both out over FADE_OUT_MS
//   'in'   — easel photoVisible=true, onion opacity=user; CSS transition
//            fades back in over FADE_IN_MS, after which we return to
//            'idle' (drops the onion's CSS transition so the slider
//            stays snappy outside the swap window).
const DEFAULT_ONION_OPACITY = 0.25;

const FADE_OUT_MS = LANDING_PHOTO_FADE_OUT_MS;
const FADE_IN_MS = LANDING_PHOTO_FADE_IN_MS;

export default function useCreative({ active }) {
  const [selectedMarcId, setSelectedMarcId] = React.useState(null);
  const [displayedMarcId, setDisplayedMarcId] = React.useState(null);
  const [transitionPhase, setTransitionPhase] = React.useState("idle");
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [onionOpacity, setOnionOpacity] = React.useState(DEFAULT_ONION_OPACITY);
  const [warmth, setWarmth] = React.useState(DEFAULT_EGG_WARMTH);
  const [redshift, setRedshift] = React.useState(DEFAULT_EGG_REDSHIFT);
  const [eggLights, setEggLights] = React.useState(DEFAULT_EGG_LIGHTS);
  const [palette, setPalette] = React.useState(DEFAULT_PALETTE);

  const setPaletteSlot = React.useCallback((index, hex) => {
    setPalette((prev) => {
      if (prev[index] === hex) return prev;
      const next = prev.slice();
      next[index] = hex;
      return next;
    });
  }, []);

  const fadeOutTimerRef = React.useRef(null);
  const fadeInTimerRef = React.useRef(null);
  const clearTimers = React.useCallback(() => {
    if (fadeOutTimerRef.current != null) {
      clearTimeout(fadeOutTimerRef.current);
      fadeOutTimerRef.current = null;
    }
    if (fadeInTimerRef.current != null) {
      clearTimeout(fadeInTimerRef.current);
      fadeInTimerRef.current = null;
    }
  }, []);

  // Reset on creative exit. Picker is NOT auto-opened on entry — the
  // Landing button opens the picker directly while still in landing
  // mode, and picking a marc is what triggers the landing → creative
  // transition (App handles the setMode + pickMarc call). By the time
  // we're "active" here, the user has already picked, so an auto-open
  // would just re-pop the picker. Reseeding warmth/redshift/lights to
  // per-marc author values lives in useDisplayedSurfaces.
  React.useEffect(() => {
    if (active) return;
    clearTimers();
    setSelectedMarcId(null);
    setDisplayedMarcId(null);
    setTransitionPhase("idle");
    setPickerOpen(false);
    setOnionOpacity(DEFAULT_ONION_OPACITY);
    setPalette(DEFAULT_PALETTE);
  }, [active, clearTimers]);

  // `fadeFromHint` is what the easel/onion are currently rendering at
  // pick time — pre-pick that's whatever ambient marc was on screen
  // (landing's last marc, in the common landing → creative path). The
  // hint becomes the fade-out source when displayedMarcId is still
  // null, so the very first pick after creative entry cross-fades
  // from the ambient marc instead of snapping. Without it, the first
  // pick would always take the snap path (prev==null).
  const pickMarc = React.useCallback(
    (id, fadeFromHint = null) => {
      setSelectedMarcId(id);
      setPickerOpen(false);

      setDisplayedMarcId((prev) => {
        const fadeFrom = prev ?? fadeFromHint;
        // No fade source (somehow neither displayed nor ambient) or
        // same-marc re-pick: snap.
        if (fadeFrom == null || fadeFrom === id) {
          clearTimers();
          setTransitionPhase("idle");
          return id;
        }

        // Cancel any in-flight transition so a rapid second pick
        // doesn't race the prior swap.
        clearTimers();
        setTransitionPhase("out");
        fadeOutTimerRef.current = setTimeout(() => {
          fadeOutTimerRef.current = null;
          setDisplayedMarcId(id);
          setTransitionPhase("in");
          fadeInTimerRef.current = setTimeout(() => {
            fadeInTimerRef.current = null;
            setTransitionPhase("idle");
          }, FADE_IN_MS);
        }, FADE_OUT_MS);
        // Park displayedMarcId on the fade-out source so the easel +
        // onion render that marc through the fade-out window.
        return fadeFrom;
      });
    },
    [clearTimers],
  );

  return {
    selectedMarcId,
    displayedMarcId,
    // Exposed so App can seed it on creative entry to whatever marc is
    // currently on screen (landing's last). Seeding lets useReferenceJson
    // fetch the ambient reference before the user clicks, so the fade-
    // out's first frame already has the correct marc transform — without
    // it, displayedEff briefly resolves to DEFAULT_MARC_TRANSFORM during
    // the React commit between marcId-set and fetch-resolve.
    setDisplayedMarcId,
    transitionPhase,
    pickMarc,
    pickerOpen,
    openPicker: () => setPickerOpen(true),
    closePicker: () => setPickerOpen(false),
    onionOpacity,
    setOnionOpacity,
    warmth,
    setWarmth,
    redshift,
    setRedshift,
    eggLights,
    setEggLights,
    palette,
    setPalette,
    setPaletteSlot,
  };
}
